import { datadogRum } from '@datadog/browser-rum';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import {APP_HERO_PRODUCTS, SCREENS, FEATURE_FLAGS, FS_FEATURE_FLAGS} from '@white-label-configuration/constants';

import type {
  LDClient,
  LDContextCommon,
  LDEvaluationDetail,
  LDFlagValue,
  LDMultiKindContext,
  LDFlagSet,
  initialize,
  LDInspection
} from 'launchdarkly-js-client-sdk';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';

let ldClient: {
  initialize: typeof initialize;
  version: string
};

if (process.client) {
  ldClient = require('launchdarkly-js-client-sdk');
} else {
  ldClient = require('launchdarkly-node-client-sdk');
}


// The client
let client: LDClient | undefined = undefined;

/**
 * Create the launch darkly context
 */
export const createLaunchDarklyContext = (): LDMultiKindContext => {
  const organisation: LDContextCommon = {
    kind: 'organisation',
    key: 'organisation',
    channelToken: getAppVariable('channel.token'),
  }

  const partner: LDContextCommon = {
    kind: 'partner',
    key: 'partner-' +  getAppVariable('partner_id', -1),
    id: getAppVariable('partner_id', -1),
    name: getAppVariable('partner_name', 'missing-partner-name'),
    typeCode: getAppVariable('partner_type_code', 'white_label'),
  }

  const channel: LDContextCommon = {
    kind: 'channel',
    key: getAppVariable('channel.token'),
    name: 'white label channel',
    token: getAppVariable('channel.token'),
    productTypesAvailable: getAppVariable('product_types_available', []),
    primaryProductType: getAppHeroProduct(APP_HERO_PRODUCTS.PARKING),
  }

  const service: LDContextCommon = {
    kind: 'service',
    key: 'cavu-white-label',
    name: 'White Label'
  }

  let deviceType;
  if (window.innerWidth < SCREENS.MD) {
    deviceType = 'mobile';
  } else if (SCREENS.MD <= window.innerWidth && window.innerWidth < SCREENS.LG) {
    deviceType = 'tablet';
  } else {
    deviceType = 'desktop';
  }
  const device: LDContextCommon = {
    kind: 'device',
    key: "device",
    deviceType
  }

  const is_checkout_page: LDContextCommon = {
    kind: 'is_checkout_page',
    key: 'is-checkout-page',
    isCheckoutPage: window?.location?.pathname.includes('/checkout/'),
  }

  const {
    code = 'unknown_region_code',
    name = 'unknown_region_name',
  }: {
    code?: string,
    name?: string,
  } = window.regionConfig || {};
  const region: LDContextCommon = {
    kind: 'region',
    key: `region-${code}-${name.replace(' ', '_')}`,
    code,
    name,
  }

  const {
    branch = 'unknown_branch',
    featureOrMaster = 'unknown_branch_type',
    internalOrExternal = 'unknown_domain_type',
  }: {
    branch?: string;
    featureOrMaster?: string;
    internalOrExternal?: string;
  } = window;

  const user: LDContextCommon = {
    kind: 'user',
    anonymous: true,
    branch,
    featureOrMaster,
    internalOrExternal,
  }

  return {
    kind: 'multi',
    channel,
    organisation,
    partner,
    service,
    device,
    region,
    user,
    is_checkout_page,
  };
}

/**
 * Get the client
 *
 * @returns {LDClient | undefined}
 */
export const getLaunchDarklyClient = () => {
  if (client !== undefined) {
    return client;
  }

  if (process.env['NUXT_ENV_LAUNCH_DARKLY_CLIENT_ID'] === undefined) {
    console.warn('Launch darkly client ID is missing, feature flags will not work');
    return;
  }

  const evaluatedSessionFlags: { [path: string]: { [flagKey: string]: unknown } } = {};
  const fsInspector: LDInspection = {
    type: 'flag-used',
    name: 'fullstory-inspector',
    method: (key: string, detail: LDEvaluationDetail) => {
      // FullStory attaches getCurrentSessionURL and setVars to the window object.
      // https://help.fullstory.com/hc/en-us/articles/1500004101581-FS-setVars-API-Sending-custom-page-data-to-FullStory
      if (window.FS === undefined || typeof window.FS.getCurrentSessionURL !== 'function') {
        return;
      }

      const url = window.FS.getCurrentSessionURL(true);
      if (url === undefined || !FS_FEATURE_FLAGS.includes(key)) {
        return;
      }

      evaluatedSessionFlags[url] = {
        ...evaluatedSessionFlags[url],
        [key]: detail.value,
      };
      const arrayOfFlags = Object.keys(evaluatedSessionFlags[url]).map(
          (flagKey) => `${flagKey}=${JSON.stringify(evaluatedSessionFlags[url][flagKey], null, 1)}`,
      );
      if (typeof window.FS.setVars === 'function') {
        window.FS.setVars('page', { ldflags: arrayOfFlags }, {integration:"launchdarkly"});
      }
    },
  };

  const context = createLaunchDarklyContext()

  client = ldClient.initialize(process.env['NUXT_ENV_LAUNCH_DARKLY_CLIENT_ID'], context, {
    streaming: true,
    inspectors: [
      {
        type: 'flag-used',
        name: 'dd-inspector',
        method: (key: string, detail: LDEvaluationDetail) => {
          datadogRum.addFeatureFlagEvaluation(key, detail.value);
        }
      },
      fsInspector,
    ]
  });

  return client
}

/**
 * Get the client
 *
 * @returns {void}
 */
export const closeLaunchDarklyClient = async () => {
  if (client !== undefined) {
    await client.close();
    setLaunchDarklyClient(undefined);
  }
}

/**
 * Set the client
 *
 * @param newClient
 * @returns {void}
 */
export const setLaunchDarklyClient = (newClient: LDClient | undefined) => {
  client = newClient
}

/**
 * Check the value of a feature flag
 *
 * DEVELOPER NOTE:
 * This function implements the falsey by default policy for feature toggles :)
 *
 *
 * @returns {false | any}
 */
export const getFeatureFlag = (flag: string, defaultValue?: LDFlagValue) => {
  if (process.client === false) {
    return false;
  }

  const client = getLaunchDarklyClient();

  if (client === undefined) {
    return false;
  }

  return client.variation(flag, defaultValue)
}

/**
 * Track LaunchDarkly custom events
 *
 * @param flag
 * @param eventName
 * @returns {void}
 */
export const trackLaunchDarklyEvent = (eventName: string): void => {
  const client = getLaunchDarklyClient();

  if (client !== undefined) {
      client.track(eventName);
  }
}

/**
 * Flush LaunchDarkly events
 *
 * @returns {void}
 */
export const flushLaunchDarklyEvents = async () => {
  const client = getLaunchDarklyClient();

  if (client !== undefined) {
    try {
      await client.flush();
    } catch (err) {
      console.error('Error while flushing client:', err);
    }
  }
}

/**
 * Check if experiment for flag is active
 *
 * @param flag
 * @returns {true | false}
 */
export const isInExperiment = (flag: string): boolean => {
  const client = getLaunchDarklyClient();

  if (client === undefined) {
    return false;
  }

  return client.variationDetail(flag)?.reason?.inExperiment === true;
}

/**
 * Identify the launch darkly client with a new context, whilst merging
 * with the original context.
 * @param newContext The new context to identify
 */
export const identifyWithNewContext = async (newContext: LDContextCommon)=> {
  const client = getLaunchDarklyClient();

  if (client === undefined) {
    return;
  }

  const launchDarklyContext = {
    ...createLaunchDarklyContext(),
    ...newContext,
  }

  return new Promise<LDFlagSet | null>((resolve, reject) => {
    client.identify(launchDarklyContext, undefined, (error, flags) => {
      if (error) {
        console.error('Error identifying with new LD context:', error);
        reject(error);
      }
      console.log('Identified with new LD context:', client.getContext());
      resolve(flags);
    });
  });
}

/**
 * Evaluates a feature flag after identifying with a new context.
 * Can be used to avoid race conditions between identifying a context
 * with LaunchDarkly and Vue reactivity hooks.
 *
 * @param flagKey The key of the flag to check
 * @param newContext The new context to identify with
 * @param defaultValue The default value to return if the flag check is unsuccessful
 * @returns The evaluated flag value
 */
export const getFeatureFlagWithNewContext = async (flagKey: string, newContext: LDContextCommon, defaultValue?: LDFlagValue) => {
  let flags: LDFlagSet | null | undefined = null;
  try {
    flags = await identifyWithNewContext(newContext);
    if (!flags) return defaultValue;
  } catch (error) {
    console.error('Error identifying with new LD context:', error);
    return defaultValue;
  }

  return flags[flagKey] ?? defaultValue;
}

export const IsAp1480MoveCheckoutPagesIntoTheEcommerceAppEnabled = () => {
  return Boolean(getFeatureFlag(FEATURE_FLAGS.AP_1480_MOVE_CHECKOUT_PAGES_INTO_THE_ECOMMERCE_APP, false));
}

export const isGtm1216PriorityPassLoungesCollinsonIntegrationEnabled = () => {
  return Boolean(getFeatureFlag(FEATURE_FLAGS.GTM_1216_PRIORITY_PASS_LOUNGES_COLLINSON_INTEGRATION, false));
}

export const isAcq87AdditionalLanguagesEnabled = () => {
  return Boolean(
    getFeatureFlag(FEATURE_FLAGS.ACQ_87_ADD_DANISH_SWEDISH_TURKISH_AND_NORWEGIAN_LANGUAGES_TO_EXTRANET, false),
  );
}
